import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Database, Plug, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { ConfirmModal, ActiveBadge, BTN_PRIMARY } from "components/admin/AdminUI";
import api from "lib/api";

export default function OpenSearchConfig() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tests, setTests] = useState({}); // {id: "testing"|"ok"|"fail"}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/opensearch/");
      setList(data.results || data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function test(id) {
    setTests((t) => ({ ...t, [id]: "testing" }));
    try {
      const { data } = await api.post(`/admin/opensearch/${id}/test`);
      setTests((t) => ({ ...t, [id]: data.ok ? "ok" : "fail" }));
    } catch {
      setTests((t) => ({ ...t, [id]: "fail" }));
    }
  }

  async function confirmDelete() {
    setBusy(true);
    try {
      await api.delete(`/admin/opensearch/${deleting.id}/`);
      setDeleting(null);
      load();
    } finally {
      setBusy(false);
    }
  }

  const TestState = ({ id }) => {
    const s = tests[id];
    if (s === "testing") return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    if (s === "ok") return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (s === "fail") return <XCircle className="h-4 w-4 text-red-500" />;
    return null;
  };

  const Actions = ({ c }) => (
    <div className="flex items-center gap-1">
      <button
        onClick={() => test(c.id)}
        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title="Testar conexão"
      >
        <Plug className="h-4 w-4" />
      </button>
      <button
        onClick={() => navigate(`/admin/opensearch/${c.id}/edit`)}
        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title="Editar"
      >
        <Pencil className="h-4 w-4" />
      </button>
      <button
        onClick={() => setDeleting(c)}
        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
        title="Excluir"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );

  return (
    <div>
      <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Conexões OpenSearch</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Cluster consultado pelas buscas. A conexão <b>ativa</b> é a usada.
          </p>
        </div>
        <button onClick={() => navigate("/admin/opensearch/new")} className={BTN_PRIMARY}>
          <Plus className="h-4 w-4" /> Nova conexão
        </button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center">
            <Database className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Nenhuma conexão cadastrada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium">Endereço</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} className="border-b border-border transition-colors last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">{c.base_url}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <ActiveBadge active={c.is_active} activeLabel="Ativa" inactiveLabel="Inativa" />
                        <TestState id={c.id} />
                      </div>
                    </td>
                    <td className="px-4 py-3"><div className="flex justify-end"><Actions c={c} /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleting && (
        <ConfirmModal
          icon={Trash2}
          iconClass="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
          title="Excluir conexão?"
          confirmLabel="Excluir"
          confirmClass="bg-red-600 text-white hover:bg-red-700"
          loading={busy}
          onClose={() => setDeleting(null)}
          onConfirm={confirmDelete}
        >
          Tem certeza que deseja excluir <strong>{deleting.name}</strong>?
        </ConfirmModal>
      )}
    </div>
  );
}
